import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { ArrowLeft, Calendar, User, Share2 } from 'lucide-react';
import { blogService } from '../services/db';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const BlogPost = () => {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPost = async () => {
            try {
                const fetchedPost = await blogService.getPostBySlug(slug);
                if (fetchedPost) {
                    setPost(fetchedPost);
                } else {
                    setError("Artículo no encontrado.");
                }
            } catch (err) {
                console.error("Failed to fetch blog post:", err);
                setError("No se pudo cargar el artículo.");
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
    }, [slug]);

    if (loading) {
        return (
            <Layout>
                <div className="flex-1 flex flex-col h-full items-center justify-center">
                    <span className="loading loading-spinner loading-lg text-primary">Cargando...</span>
                </div>
            </Layout>
        );
    }

    if (error || !post) {
        return (
            <Layout>
                <div className="flex-1 flex flex-col h-full items-center justify-center">
                    <p className="text-red-400 mb-4">{error || "Artículo no encontrado"}</p>
                    <Link to="/blog" className="text-primary hover:underline flex items-center">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Volver al blog
                    </Link>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="flex-1 overflow-y-auto p-0 -m-4 md:-m-8">
                <div className="-m-4 md:-m-8">
                    {/* Hero Section */}
                    <div className="relative w-full h-[40vh] min-h-[400px]">
                        {post.imageUrl && (
                            <>
                                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${post.imageUrl})` }} />
                                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
                            </>
                        )}
                        {!post.imageUrl && (
                            <div className="absolute inset-0 bg-zinc-900 bg-gradient-to-br from-zinc-900 to-black" />
                        )}

                        <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-12 max-w-4xl mx-auto">
                            <Link to="/blog" className="inline-flex items-center text-zinc-400 hover:text-white mb-6 transition-colors w-fit">
                                <ArrowLeft className="w-4 h-4 mr-2" /> Volver al blog
                            </Link>

                            <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-6 leading-tight drop-shadow-lg">
                                {post.title}
                            </h1>

                            <div className="flex flex-wrap items-center gap-6 text-sm text-zinc-300 font-mono">
                                {post.publishedAt && (
                                    <span className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-primary" />
                                        {new Date(post.publishedAt).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
                                    </span>
                                )}
                                {post.author && (
                                    <span className="flex items-center gap-2">
                                        <User className="w-4 h-4 text-primary" />
                                        {post.author}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Content Section */}
                    <div className="max-w-3xl mx-auto p-8 md:p-12">
                        <article className="prose max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {post.content}
                            </ReactMarkdown>
                        </article>

                        <div className="mt-16 pt-8 border-t border-zinc-800 flex justify-between items-center">
                            <Link to="/blog" className="text-zinc-400 hover:text-white transition-colors flex items-center group">
                                <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" /> Más artículos
                            </Link>
                            <button className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors text-sm font-medium text-white">
                                <Share2 className="w-4 h-4" /> Compartir
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default BlogPost;
